import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EnhancedHeader } from '@/components/common/EnhancedHeader';
import { Edit, Save, X, Camera } from 'lucide-react';
import { apiUrl } from '@/config/api';
import InternationalPhoneInput from '@/components/common/InternationalPhoneInput';
import AddressInput from '@/components/common/AddressInput';

interface ProfileData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  countryCode?: string;
  address?: {
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  avatar?: string;
  preferences?: {
    notifications: boolean;
    marketing: boolean;
    language: string;
  };
}

const CustomerProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Always use user data from auth context as primary source
      if (user) {
        const baseProfileData = {
          id: user.id,
          name: user.name,
          email: user.email,
          phone: '',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'India'
          },
          preferences: {
            notifications: true,
            marketing: false,
            language: 'en'
          }
        };

        // Try to fetch additional profile data from API
        if (token) {
          try {
            const response = await fetch(apiUrl('/api/customer/profile'), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const apiData = await response.json();
              console.log('Profile API response:', apiData);
              
              // Merge API data with user data, but don't override critical fields
              setProfileData({
                ...baseProfileData,
                name: apiData.user?.full_name || baseProfileData.name, // Use full_name from API
                phone: apiData.user?.phone_number || '',
                avatar: apiData.user?.avatar_url || '', // Load avatar from API
                address: {
                  street: apiData.user?.address_line1 || '',
                  city: apiData.user?.city || '',
                  state: apiData.user?.state || '',
                  zipCode: apiData.user?.postal_code || '',
                  country: apiData.user?.country || 'India'
                },
                preferences: {
                  notifications: apiData.customer_profile?.order_updates ?? true,
                  marketing: apiData.customer_profile?.marketing_communications ?? false,
                  language: apiData.customer_profile?.language_preference || 'en'
                }
              });
              return;
            } else {
              console.log('Profile API failed with status:', response.status);
            }
          } catch (apiError) {
            console.log('API fetch failed, using user data:', apiError);
          }
        }

        // Use base profile data if API fails or no token
        setProfileData(baseProfileData);
      }
    } catch (_error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profileData) return;

    try {
      setIsSaving(true);
      const token = localStorage.getItem('authToken');
      
      // Transform data to match backend API structure
      const updateData = {
        full_name: profileData.name,
        phone_number: profileData.phone,
        address_line1: profileData.address?.street,
        address_line2: '',
        city: profileData.address?.city,
        state: profileData.address?.state,
        postal_code: profileData.address?.zipCode,
        country: profileData.address?.country,
        avatar_url: profileData.avatar, // Include avatar data
        preferred_contact_method: 'email',
        marketing_communications: profileData.preferences?.marketing,
        order_updates: profileData.preferences?.notifications,
        language_preference: profileData.preferences?.language
      };
      
      console.log('Sending profile update:', updateData);
      
      const response = await fetch(apiUrl('/api/customer/profile'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Profile update response:', result);
        
        // Update user data in localStorage and auth context
        const updatedUser = {
          ...user,
          name: profileData.name,
          email: profileData.email
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setIsEditing(false);
        setError(null);
        
        // Reload profile data to show updated information
        await loadProfile();
        
        // Show success message
        alert('Profile updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('Profile update failed:', errorData);
        setError(`Failed to save profile: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('Failed to save profile:', error);
      setError('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadProfile(); // Reload original data
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      // Convert to base64 for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfileData({
          ...profileData!,
          avatar: result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <EnhancedHeader title="Profile" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <EnhancedHeader title="Profile" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-gray-600 dark:text-slate-400">Profile not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <EnhancedHeader title="Profile" showBackButton backTo="/customer/dashboard" />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                  {profileData.avatar ? (
                    <img 
                      src={profileData.avatar} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    profileData.name?.charAt(0) || 'U'
                  )}
                </div>
                {isEditing && (
                  <label className="absolute -bottom-1 -right-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 transition-colors cursor-pointer">
                    <Camera className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <div className="text-white">
                <h1 className="text-2xl font-bold">{profileData.name}</h1>
                <p className="text-blue-100">{profileData.email}</p>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white mt-2">
                  Customer
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end mb-6">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Profile Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 border-b border-gray-200 dark:border-slate-700 pb-2">
                  Personal Information
                </h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-100"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-slate-100">{profileData.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={profileData.email}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-xs text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded">
                        Cannot be changed
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <InternationalPhoneInput
                      value={profileData.phone || ''}
                      onChange={(phone, isValid) => {
                        setProfileData({ ...profileData, phone });
                        if (!isValid && phone) {
                          // Handle validation error if needed
                        }
                      }}
                      placeholder="Enter phone number"
                      defaultCountry="in"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-slate-100">
                      {profileData.phone || 'Not provided'}
                    </p>
                  )}
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 border-b border-gray-200 dark:border-slate-700 pb-2">
                  Address Information
                </h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Address
                  </label>
                  {isEditing ? (
                    <AddressInput
                      value={profileData.address || {}}
                      onChange={(addressData) => {
                        setProfileData({
                          ...profileData,
                          address: addressData
                        });
                      }}
                      placeholder="Enter your address"
                      countryRestrictions={['in']}
                      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-slate-100">
                      {profileData.address?.address || 'Not provided'}
                    </p>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
